import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialProjectState } from 'store/initial-states';
import {
  fetchProjects,
  fetchMoreProjects,
  fetchAllProjects,
  fetchProjectById,
  createProject,
  updateProject,
  deleteProject,
  inviteProjectMember,
  removeProjectMember,
  updateProjectMemberRole,
  acceptProjectInvitation,
  declineProjectInvitation,
  fetchPendingInvitations,
  fetchProjectDetailsTasks,
  leaveProject,
  deleteProjectDetailsTask
} from './extra';
import {
  Project,
  ProjectDetailsFilters
} from 'interfaces/project.interface';
import { PaginationMeta } from 'components/ui/PaginationCompact';
import { mergeProjectsById, uniqueProjectsById } from 'utils/projects/project.util';
import { projectMatchesListStatusFilter } from 'utils/projects/project-lifecycle.util';
import { ToDoTask } from 'interfaces/common.interface';
import {
  ProjectDetailsViewMode,
  ProjectDetailTab,
  ProjectLifecycleStatus,
  ProjectsListViewMode,
  SORT_MODE
} from 'enums';

const project = createSlice({
  name: 'project',
  initialState: initialProjectState,
  reducers: {
    updateProjects: (state, { payload }: PayloadAction<Project[]>) => {
      state.projects = payload;
    },
    updateCurrentProject: (
      state,
      { payload }: PayloadAction<Project | null>
    ) => {
      state.currentProject = payload;
    },
    updateIsProjectsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isProjectsLoading = payload;
    },
    updateIsProjectLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isProjectLoading = payload;
    },
    updateIsCreatingProject: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreatingProject = payload;
    },
    updateIsUpdatingProject: (state, { payload }: PayloadAction<boolean>) => {
      state.isUpdatingProject = payload;
    },
    updateIsDeletingProject: (state, { payload }: PayloadAction<boolean>) => {
      state.isDeletingProject = payload;
    },
    updateIsInvitingMember: (state, { payload }: PayloadAction<boolean>) => {
      state.isInvitingMember = payload;
    },
    updateIsUpdatingMember: (state, { payload }: PayloadAction<boolean>) => {
      state.isUpdatingMember = payload;
    },
    updateIsRemovingMember: (state, { payload }: PayloadAction<boolean>) => {
      state.isRemovingMember = payload;
    },
    updateIsAcceptingInvitation: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isAcceptingInvitation = payload;
    },
    updateIsDecliningInvitation: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isDecliningInvitation = payload;
    },
    updateShowCreateProjectModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showCreateProjectModal = payload;
    },
    updateShowEditProjectModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showEditProjectModal = payload;
    },
    updateShowInviteMemberModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showInviteMemberModal = payload;
    },
    updateShowProjectDetailsModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showProjectDetailsModal = payload;
    },
    updatePagination: (
      state,
      { payload }: PayloadAction<PaginationMeta | null>
    ) => {
      state.pagination = payload;
    },
    updateProjectDetailsTasks: (
      state,
      { payload }: PayloadAction<ToDoTask[]>
    ) => {
      state.projectDetails.tasks = payload;
    },
    // Merge one task into project details (optimistic Kanban/status updates). Full list refresh → updateProjectDetailsTasks.
    mergeProjectDetailsTask: (state, { payload }: PayloadAction<ToDoTask>) => {
      const index = state.projectDetails.tasks.findIndex(
        (task) => task.id === payload.id
      );
      if (index === -1) {
        state.projectDetails.tasks.push(payload);
        return;
      }
      state.projectDetails.tasks[index] = {
        ...state.projectDetails.tasks[index],
        ...payload
      };
    },
    updateProjectDetailsActiveTab: (
      state,
      { payload }: PayloadAction<ProjectDetailTab>
    ) => {
      state.projectDetails.activeTab = payload;
    },
    resetProjectDetails: (state) => {
      state.projectDetails.tasks = [];
      state.projectDetails.isTasksLoading = false;
      state.projectDetails.tasksError = null;
      state.projectDetails.activeTab = ProjectDetailTab.TASKS;
      state.projectDetails.showFilters = false;
      state.projectDetails.filters = {
        taskName: '',
        perspirationLevel: null,
        dueDateRange: null,
        sortMode: SORT_MODE.SMART
      };
      state.projectDetails.showMembersModal = false;
      state.projectDetails.showSettingsDrawer = false;
      state.projectDetails.showTaskDrawer = false;
      state.projectDetails.selectedTaskToEdit = null;
      state.projectDetails.isCreatingTask = false;
      state.projectDetails.isUpdatingTask = false;
      state.projectDetails.isDeletingTask = false;
      state.projectDetails.isTaskStatusUpdating = false;
      state.projectDetails.taskStatusUpdatingIds = [];
    },
    updateProjectDetailsViewMode: (
      state,
      { payload }: PayloadAction<ProjectDetailsViewMode>
    ) => {
      state.projectDetails.mode = payload;
    },
    updateProjectDetailsShowFilters: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.projectDetails.showFilters = payload;
    },
    updateProjectDetailsFilters: (
      state,
      { payload }: PayloadAction<Partial<ProjectDetailsFilters>>
    ) => {
      state.projectDetails.filters = {
        ...state.projectDetails.filters,
        ...payload
      };
    },
    // Panel-only reset: taskName lives in the toolbar search and sortMode in the
    // sort toggle, so both survive closing the filters popover.
    resetProjectDetailsFilters: (state) => {
      state.projectDetails.filters.perspirationLevel = null;
      state.projectDetails.filters.dueDateRange = null;
    },
    updateProjectDetailsShowMembersModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.projectDetails.showMembersModal = payload;
      if (payload) {
        state.projectDetails.showSettingsDrawer = false;
      }
    },
    updateProjectDetailsShowSettingsDrawer: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.projectDetails.showSettingsDrawer = payload;
      if (payload) {
        state.projectDetails.showMembersModal = false;
      }
    },
    updateProjectDetailsShowTaskDrawer: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.projectDetails.showTaskDrawer = payload;
    },
    updateProjectDetailsSelectedTaskToEdit: (
      state,
      { payload }: PayloadAction<ToDoTask | null>
    ) => {
      state.projectDetails.selectedTaskToEdit = payload;
    },
    updateProjectDetailsIsCreatingTask: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.projectDetails.isCreatingTask = payload;
    },
    updateProjectDetailsIsUpdatingTask: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.projectDetails.isUpdatingTask = payload;
    },
    updateProjectDetailsIsTaskStatusUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.projectDetails.isTaskStatusUpdating = payload;
      if (!payload) {
        state.projectDetails.taskStatusUpdatingIds = [];
      }
    },
    addProjectDetailsTaskStatusUpdatingId: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      if (state.projectDetails.taskStatusUpdatingIds.includes(payload)) {
        return;
      }
      state.projectDetails.taskStatusUpdatingIds.push(payload);
      state.projectDetails.isTaskStatusUpdating = true;
    },
    removeProjectDetailsTaskStatusUpdatingId: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      state.projectDetails.taskStatusUpdatingIds =
        state.projectDetails.taskStatusUpdatingIds.filter(
          (taskId) => taskId !== payload
        );
      state.projectDetails.isTaskStatusUpdating =
        state.projectDetails.taskStatusUpdatingIds.length > 0;
    },
    updateProjectDetailsIsDeletingTask: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.projectDetails.isDeletingTask = payload;
    },
    updateDebouncedSearch: (state, { payload }: PayloadAction<string>) => {
      state.debouncedSearch = payload;
    },
    updateShowCapacitySettings: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showCapacitySettings = payload;
    },
    updateShowPendingInvitationsDrawer: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showPendingInvitationsDrawer = payload;
    },
    updateProjectsListViewMode: (
      state,
      { payload }: PayloadAction<ProjectsListViewMode>
    ) => {
      state.listViewMode = payload;
    },
    updateListStatusFilter: (
      state,
      { payload }: PayloadAction<ProjectLifecycleStatus>
    ) => {
      state.listStatusFilter = payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.isProjectsLoading = true;
      })
      .addCase(fetchProjects.fulfilled, (state, { payload }) => {
        if (payload) {
          state.projects = uniqueProjectsById(payload.data || []);
          state.pagination = payload.meta || null;
        }
        state.isProjectsLoading = false;
      })
      .addCase(fetchProjects.rejected, (state) => {
        state.projects = [];
        state.pagination = null;
        state.isProjectsLoading = false;
      })
      .addCase(fetchMoreProjects.pending, (state) => {
        state.isLoadingMoreProjects = true;
      })
      .addCase(fetchMoreProjects.fulfilled, (state, { payload }) => {
        if (payload) {
          state.projects = mergeProjectsById(
            state.projects,
            payload.data || []
          );
          state.pagination = payload.meta || null;
        }
        state.isLoadingMoreProjects = false;
      })
      .addCase(fetchMoreProjects.rejected, (state) => {
        state.isLoadingMoreProjects = false;
      })
      .addCase(fetchAllProjects.pending, (state) => {
        state.isProjectsLoading = true;
      })
      .addCase(fetchAllProjects.fulfilled, (state, { payload }) => {
        if (payload) {
          state.projects = payload.data || [];
          state.pagination = payload.meta;
        }
        state.isProjectsLoading = false;
      })
      .addCase(fetchAllProjects.rejected, (state) => {
        state.projects = [];
        state.pagination = null;
        state.isProjectsLoading = false;
      })
      .addCase(fetchProjectById.pending, (state) => {
        state.isProjectLoading = true;
      })
      .addCase(fetchProjectById.fulfilled, (state, { payload }) => {
        if (payload) {
          state.currentProject = payload;
          state.projects = state.projects.map((project) =>
            project.id === payload.id ? payload : project
          );
        }
        state.isProjectLoading = false;
      })
      .addCase(fetchProjectById.rejected, (state) => {
        state.currentProject = null;
        state.isProjectLoading = false;
      })
      .addCase(createProject.pending, (state) => {
        state.isCreatingProject = true;
      })
      .addCase(createProject.fulfilled, (state, { payload }) => {
        if (payload) {
          state.projects = [...state.projects, payload];
        }
        state.isCreatingProject = false;
        state.showCreateProjectModal = false;
      })
      .addCase(createProject.rejected, (state) => {
        state.isCreatingProject = false;
      })
      .addCase(updateProject.pending, (state) => {
        state.isUpdatingProject = true;
      })
      .addCase(updateProject.fulfilled, (state, { payload }) => {
        if (payload) {
          const matchesFilter = projectMatchesListStatusFilter(
            payload,
            state.listStatusFilter
          );
          const projectIndex = state.projects.findIndex(
            (project) => project.id === payload.id
          );
          if (matchesFilter && projectIndex >= 0) {
            state.projects[projectIndex] = payload;
          } else if (!matchesFilter && projectIndex >= 0) {
            state.projects = state.projects.filter(
              (project) => project.id !== payload.id
            );
          }
          if (state.currentProject?.id === payload.id) {
            state.currentProject = payload;
          }
        }
        state.isUpdatingProject = false;
        state.showEditProjectModal = false;
      })
      .addCase(updateProject.rejected, (state) => {
        state.isUpdatingProject = false;
      })
      .addCase(deleteProject.pending, (state) => {
        state.isDeletingProject = true;
      })
      .addCase(deleteProject.fulfilled, (state, { payload }) => {
        if (payload) {
          state.projects = state.projects.filter((p) => p.id !== payload);
          if (state.currentProject?.id === payload) {
            state.currentProject = null;
          }
        }
        state.isDeletingProject = false;
      })
      .addCase(deleteProject.rejected, (state) => {
        state.isDeletingProject = false;
      })
      .addCase(inviteProjectMember.pending, (state) => {
        state.isInvitingMember = true;
      })
      .addCase(inviteProjectMember.fulfilled, (state) => {
        state.isInvitingMember = false;
        state.showInviteMemberModal = false;
      })
      .addCase(inviteProjectMember.rejected, (state) => {
        state.isInvitingMember = false;
      })
      .addCase(removeProjectMember.pending, (state) => {
        state.isRemovingMember = true;
      })
      .addCase(removeProjectMember.fulfilled, (state) => {
        state.isRemovingMember = false;
      })
      .addCase(removeProjectMember.rejected, (state) => {
        state.isRemovingMember = false;
      })
      .addCase(updateProjectMemberRole.pending, (state) => {
        state.isUpdatingMember = true;
      })
      .addCase(updateProjectMemberRole.fulfilled, (state) => {
        state.isUpdatingMember = false;
      })
      .addCase(updateProjectMemberRole.rejected, (state) => {
        state.isUpdatingMember = false;
      })
      .addCase(acceptProjectInvitation.pending, (state) => {
        state.isAcceptingInvitation = true;
      })
      .addCase(acceptProjectInvitation.fulfilled, (state) => {
        state.isAcceptingInvitation = false;
      })
      .addCase(acceptProjectInvitation.rejected, (state) => {
        state.isAcceptingInvitation = false;
      })
      .addCase(declineProjectInvitation.pending, (state) => {
        state.isDecliningInvitation = true;
      })
      .addCase(declineProjectInvitation.fulfilled, (state) => {
        state.isDecliningInvitation = false;
      })
      .addCase(declineProjectInvitation.rejected, (state) => {
        state.isDecliningInvitation = false;
      })
      .addCase(leaveProject.pending, (state) => {
        state.isLeavingProject = true;
      })
      .addCase(leaveProject.fulfilled, (state, { payload }) => {
        state.isLeavingProject = false;
        if (payload) {
          state.projects = state.projects.filter(
            (project) => project.id !== payload
          );
          if (state.currentProject?.id === payload) {
            state.currentProject = null;
          }
        }
      })
      .addCase(leaveProject.rejected, (state) => {
        state.isLeavingProject = false;
      })
      .addCase(deleteProjectDetailsTask.pending, (state) => {
        state.projectDetails.isDeletingTask = true;
      })
      .addCase(deleteProjectDetailsTask.fulfilled, (state) => {
        state.projectDetails.isDeletingTask = false;
      })
      .addCase(deleteProjectDetailsTask.rejected, (state) => {
        state.projectDetails.isDeletingTask = false;
      })
      .addCase(fetchPendingInvitations.pending, (state) => {
        state.isPendingInvitationsLoading = true;
      })
      .addCase(fetchPendingInvitations.fulfilled, (state, { payload }) => {
        state.pendingInvitations = payload;
        state.isPendingInvitationsLoading = false;
      })
      .addCase(fetchPendingInvitations.rejected, (state) => {
        state.pendingInvitations = [];
        state.isPendingInvitationsLoading = false;
      })
      .addCase(fetchProjectDetailsTasks.pending, (state) => {
        state.projectDetails.isTasksLoading = true;
        state.projectDetails.tasksError = null;
      })
      .addCase(fetchProjectDetailsTasks.fulfilled, (state, { payload }) => {
        state.projectDetails.tasks = payload ?? [];
        state.projectDetails.isTasksLoading = false;
        state.projectDetails.tasksError = payload
          ? null
          : 'failed_to_fetch_tasks';
      })
      .addCase(fetchProjectDetailsTasks.rejected, (state) => {
        state.projectDetails.tasks = [];
        state.projectDetails.isTasksLoading = false;
        state.projectDetails.tasksError = 'failed_to_fetch_tasks';
      });
  }
});

export const {
  updateProjects,
  updateCurrentProject,
  updateIsProjectsLoading,
  updateIsProjectLoading,
  updateIsCreatingProject,
  updateIsUpdatingProject,
  updateIsDeletingProject,
  updateIsInvitingMember,
  updateIsUpdatingMember,
  updateIsRemovingMember,
  updateIsAcceptingInvitation,
  updateIsDecliningInvitation,
  updateShowCreateProjectModal,
  updateShowEditProjectModal,
  updateShowInviteMemberModal,
  updateShowProjectDetailsModal,
  updatePagination,
  updateProjectDetailsTasks,
  mergeProjectDetailsTask,
  updateProjectDetailsActiveTab,
  resetProjectDetails,
  updateProjectDetailsViewMode,
  updateProjectDetailsShowFilters,
  updateProjectDetailsFilters,
  resetProjectDetailsFilters,
  updateProjectDetailsShowMembersModal,
  updateProjectDetailsShowSettingsDrawer,
  updateProjectDetailsShowTaskDrawer,
  updateProjectDetailsSelectedTaskToEdit,
  updateProjectDetailsIsCreatingTask,
  updateProjectDetailsIsUpdatingTask,
  updateProjectDetailsIsTaskStatusUpdating,
  addProjectDetailsTaskStatusUpdatingId,
  removeProjectDetailsTaskStatusUpdatingId,
  updateProjectDetailsIsDeletingTask,
  updateDebouncedSearch,
  updateShowCapacitySettings,
  updateShowPendingInvitationsDrawer,
  updateProjectsListViewMode,
  updateListStatusFilter
} = project.actions;

export default project.reducer;
